import router from 'next/router'
import React, { useEffect } from 'react'

import {
  chakra,
  Flex,
  useColorModeValue,
  useColorMode,
  Text,
  Heading,
  Box,
  VStack,
  HStack,
  Badge,
  Container,
} from '@chakra-ui/react'

import { SIGN_UP } from '@/lib/constants/authConstants'
import LEColorConstants, {
  basicTextColor,
} from '@/lib/constants/colorConstants'
import NavBar from '@/components/home/NavBar'
import CustomButton from '../Button'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { moveUp } from '@/lib/animations'
import CustomImage from '../Image/CustomImage'

type Menu = {
  name: string
  path: string
}

interface IProps {
  menus: Menu[]
  triggerNav: boolean
}

export const SchoolsHero = ({ triggerNav, menus }: IProps) => {
  const { colorMode } = useColorMode()
  const bg = useColorModeValue('white', 'black')
  const controls = useAnimation()
  const { inView, ref } = useInView()

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  const goToSignUp = () => {
    const host =
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
        ? process.env.NEXT_PUBLIC_APP_HOST
        : process.env.NEXT_PUBLIC_APP_LOCALHOST

    return router.push(host + '/signup')
  }

  return (
    <chakra.div
      id="home"
      height={{ base: 'auto', md: 'auto' }}
      w="full"
      bg={bg}
      pb="4rem"
      transition="background 0.2s"
      overflowY="hidden"
      position="relative"
      bgImage={{
        base: "url('/top-circles.svg')",
        md: "url('/semi-circle.svg'), url('/top-circles.svg')",
      }}
      bgRepeat="no-repeat, no-repeat"
      bgSize="30%, 28%"
      bgPosition="center right, top left 45px"
      as={motion.div}
      animate={controls}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      ref={ref}
    >
      <NavBar canStick={triggerNav} menus={menus as any} />

      <Container maxW="90rem" mx="auto" px={['1rem', '3rem', '4rem', '6rem']}>
        {/* Urgency Banner */}
        <Flex
          justify="center"
          mt={{ base: '6rem', md: '7.5rem' }}
          mb="2rem"
          as={motion.div}
          variants={moveUp}
        >
          <Badge
            colorScheme="red"
            variant="solid"
            fontSize={{ base: '0.85rem', md: '1.1rem' }}
            py="0.5rem"
            px="1.5rem"
            borderRadius="full"
            textAlign="center"
            animation="pulse 2s infinite"
          >
            ⚡ URGENT: WAEC & NECO Moving to CBT by 2026!
          </Badge>
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="space-between"
          as={motion.div}
          variants={moveUp}
          columnGap="2rem"
          rowGap="2rem"
          flexDir={{ base: 'column', lg: 'row' }}
        >
          <VStack
            flex={7}
            alignItems={{ base: 'center', lg: 'start' }}
            spacing="1.5rem"
          >
            <Heading
              as="h1"
              textAlign={{ base: 'center', lg: 'start' }}
              fontSize={['2rem', '2.5rem', '3rem', '3.5rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              lineHeight={['2.2rem', '2.7rem', '3.3rem', '3.8rem']}
              maxW={{ base: 'full', lg: '40rem' }}
            >
              Don't Let Your Students{' '}
              <Text as="span" color={LEColorConstants.secondary}>
                Fall Behind
              </Text>{' '}
              in the CBT Revolution
            </Heading>

            <Box
              maxW={{ base: 'full', lg: '42rem' }}
              fontSize={{ base: '1.1rem', md: '1.3rem' }}
              fontWeight="medium"
              color={basicTextColor[colorMode]}
              textAlign={{ base: 'center', lg: 'left' }}
            >
              <Text mb="1rem">
                With JAMB already fully CBT and WAEC/NECO transitioning by 2026,
                <Text
                  as="span"
                  fontWeight="bold"
                  color={LEColorConstants.primary}
                >
                  {' '}
                  your students MUST be prepared for computer-based testing
                </Text>
                .
              </Text>

              <Text>
                Schools that act now will give their students a{' '}
                <Text
                  as="span"
                  fontWeight="bold"
                  color={LEColorConstants.secondary}
                >
                  massive competitive advantage
                </Text>{' '}
                while others scramble to catch up.
              </Text>
            </Box>

            {/* Value Props */}
            <VStack
              spacing="0.8rem"
              alignItems={{ base: 'center', lg: 'start' }}
              w="full"
            >
              <HStack spacing="1rem">
                <Box
                  w="6px"
                  h="6px"
                  bg={LEColorConstants.colorCheck}
                  borderRadius="full"
                />
                <Text
                  fontSize={{ base: '1rem', md: '1.1rem' }}
                  fontWeight="medium"
                >
                  Prepare students for WAEC/NECO CBT transition
                </Text>
              </HStack>
              <HStack spacing="1rem">
                <Box
                  w="6px"
                  h="6px"
                  bg={LEColorConstants.colorCheck}
                  borderRadius="full"
                />
                <Text
                  fontSize={{ base: '1rem', md: '1.1rem' }}
                  fontWeight="medium"
                >
                  Boost exam confidence with real CBT practice
                </Text>
              </HStack>
              <HStack spacing="1rem">
                <Box
                  w="6px"
                  h="6px"
                  bg={LEColorConstants.colorCheck}
                  borderRadius="full"
                />
                <Text
                  fontSize={{ base: '1rem', md: '1.1rem' }}
                  fontWeight="medium"
                >
                  Track student progress with detailed analytics
                </Text>
              </HStack>
            </VStack>

            {/* Special Offer */}
            <Box
              bg="linear-gradient(135deg, #009BFF 0%, #FF6E06 100%)"
              p="1.5rem"
              borderRadius="12px"
              textAlign="center"
              w={{ base: 'full', lg: '85%' }}
              mt="2rem"
            >
              <Text
                color="white"
                fontSize={{ base: '1.3rem', md: '1.5rem' }}
                fontWeight="bold"
              >
                🎁 LIMITED TIME: Get 500 Credits FREE
              </Text>
              <Text
                color="white"
                fontSize={{ base: '1rem', md: '1.1rem' }}
                mt="0.5rem"
              >
                Worth ₦25,000 - Test your entire school for FREE!
              </Text>
            </Box>

            {/* CTA Buttons */}
            <HStack
              spacing="1rem"
              flexDir={{ base: 'column', sm: 'row' }}
              w="full"
              justify={{ base: 'center', lg: 'start' }}
              mt="2rem"
            >
              <CustomButton
                size="lg"
                w={{ base: 'full', sm: 'auto' }}
                minW="14rem"
                h="3.5rem"
                borderRadius="8px"
                fontWeight="bold"
                fontSize="1.1rem"
                onClick={goToSignUp}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
                transition="all 0.2s"
              >
                Claim Your 500 Free Credits
              </CustomButton>

              {/* <CustomButton
                variant="outline"
                size="lg"
                w={{ base: 'full', sm: 'auto' }}
                minW="12rem"
                h="3.5rem"
                borderRadius="8px"
                fontWeight="bold"
                fontSize="1.1rem"
                borderColor={LEColorConstants.primary}
                color={LEColorConstants.primary}
                _hover={{
                  bg: LEColorConstants.primary,
                  color: 'white',
                  transform: 'translateY(-2px)',
                }}
                transition="all 0.2s"
              >
                Watch Demo
              </CustomButton> */}
            </HStack>
          </VStack>

          {/* Hero Image */}
          <Flex
            flex={4}
            justify="center"
            align="center"
            position="relative"
            minH={{ base: '300px', lg: '400px' }}
          >
            <CustomImage
              src="/hero-illustrator.svg"
              width={450}
              height={350}
              alt="CBT Testing Platform"
              priority
            />

            {/* Floating Elements */}
            <Box
              position="absolute"
              top="10%"
              right="10%"
              bg="white"
              boxShadow="lg"
              borderRadius="lg"
              p="1rem"
              as={motion.div}
              animate={{
                y: [0, -10, 0],
              }}
              transition={
                {
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                } as any
              }
            >
              <Text
                fontSize="0.9rem"
                fontWeight="bold"
                color={LEColorConstants.primary}
              >
                ✅ WAEC/NECO Ready
              </Text>
            </Box>

            <Box
              position="absolute"
              bottom="20%"
              left="5%"
              bg="white"
              boxShadow="lg"
              borderRadius="lg"
              p="1rem"
              as={motion.div}
              animate={{
                y: [0, 10, 0],
              }}
              transition={
                {
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1,
                } as any
              }
            >
              <Text
                fontSize="0.9rem"
                fontWeight="bold"
                color={LEColorConstants.secondary}
              >
                📈 98% Success Rate
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Container>
    </chakra.div>
  )
}
